import React, { useEffect, useState } from "react";
import { getPhoneModels, lookupFirmware, aiFirmwareInsight, getFirmwareInsights } from "../lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Cpu, Search, CheckCircle2, AlertTriangle, XCircle, Bug, ChevronRight, Sparkles, History, ChevronDown } from "lucide-react";
import { PrintButton } from "../components/PrintButton";

const STATUS = {
  stable: { cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", Icon: CheckCircle2, label: "Stable" },
  buggy: { cls: "bg-amber-500/10 text-amber-400 border-amber-500/20", Icon: AlertTriangle, label: "Known Bugs" },
  eol: { cls: "bg-red-500/10 text-red-400 border-red-500/20", Icon: XCircle, label: "End of Life" },
};

const SEV_CLS = {
  high: "text-red-400 border-red-500/30 bg-red-500/10",
  medium: "text-amber-400 border-amber-500/30 bg-amber-500/10",
  low: "text-blue-400 border-blue-500/30 bg-blue-500/10",
};

export default function Firmware() {
  const [models, setModels] = useState([]);
  const [model, setModel] = useState("");
  const [version, setVersion] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [ai, setAi] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [insights, setInsights] = useState([]);
  const [openInsight, setOpenInsight] = useState(null);

  useEffect(() => { getPhoneModels().then(setModels).catch(() => {}); }, []);
  const loadInsights = () => getFirmwareInsights().then(setInsights).catch(() => {});
  useEffect(() => { loadInsights(); }, []);

  const run = async () => {
    if (!model) { setError("Select a phone model first."); return; }
    setError(""); setLoading(true); setResult(null); setAi("");
    try {
      const r = await lookupFirmware(model, version || null);
      setResult(r);
    } catch (e) {
      setError(e?.response?.data?.detail || "Lookup failed.");
    } finally { setLoading(false); }
  };

  const runAi = async () => {
    if (!model) { setError("Select a phone model first."); return; }
    setError(""); setAiLoading(true); setAi("");
    try {
      const r = await aiFirmwareInsight(model, version || null);
      setAi(r.insight);
      loadInsights();
    } catch (e) {
      setError(e?.response?.data?.detail || "AI lookup failed.");
    } finally { setAiLoading(false); }
  };

  const fmtTime = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return ""; }
  };
  const renderMd = (text) =>
    text.split("\n").filter((l) => l.trim()).map((line, i) => (
      <p key={i} className={line.trim().match(/^[-*•]/) ? "pl-4 relative before:content-['▸'] before:absolute before:left-0 before:text-blue-400" : "font-medium text-white mt-2"}>
        {line.replace(/^[-*•]\s*/, "").replace(/\*\*/g, "").replace(/^#+\s*/, "")}
      </p>
    ));

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-5 sm:px-8 bg-zinc-950/60 backdrop-blur lg:sticky lg:top-0 z-10">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
          <span className="text-zinc-600">~/</span> firmware <ChevronRight className="h-3 w-3" /> bug lookup
        </span>
      </header>

      <div className="p-5 sm:p-8 max-w-4xl">
        <div className="no-print flex justify-end mb-4"><PrintButton /></div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white tracking-tight">Firmware &amp; Version Lookup</h1>
        <p className="text-zinc-400 mt-3 max-w-2xl">
          Check a Cisco phone or Webex device (MPP phones, Room Bar / Room Kit / Board) for known bugs,
          recommended loads / firmware channels, and end-of-life status.
        </p>

        {/* Form */}
        <div className="mt-8 rounded-lg border border-zinc-800 bg-zinc-900 p-6">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-4 items-end">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Phone Model</label>
              <Select value={model} onValueChange={setModel}>
                <SelectTrigger data-testid="firmware-model-select" className="mt-2 bg-zinc-950 border-zinc-800">
                  <SelectValue placeholder="Select model…" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {models.map((m) => (
                    <SelectItem key={m.model} value={m.model} data-testid={`model-opt-${m.model}`}>
                      {m.model} · {m.protocol}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Firmware Version <span className="text-zinc-600">(optional)</span></label>
              <Input
                data-testid="firmware-version-input"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder="e.g. 12.7(1)"
                className="mt-2 bg-zinc-950 border-zinc-800 font-mono"
              />
            </div>
            <button
              onClick={run}
              data-testid="firmware-lookup-btn"
              className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 h-10 rounded-md transition-colors"
            >
              <Search className="h-4 w-4" /> Check
            </button>
          </div>
          {error && <div data-testid="firmware-error" className="mt-3 text-sm text-red-400">{error}</div>}

          <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center justify-between flex-wrap gap-3">
            <p className="text-xs text-zinc-500 max-w-md">
              Want a broader, always-fresh view beyond the curated list? Run an AI live insight for any model/version.
            </p>
            <button
              onClick={runAi}
              disabled={aiLoading}
              data-testid="firmware-ai-btn"
              className="flex items-center justify-center gap-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 text-white font-medium px-4 h-10 rounded-md transition-colors"
            >
              <Sparkles className="h-4 w-4 text-blue-400" /> {aiLoading ? "Analyzing…" : "AI Live Bug Insight"}
            </button>
          </div>
        </div>

        {ai && (
          <div className="mt-6 rounded-lg border border-blue-500/30 bg-zinc-900 p-6" data-testid="firmware-ai-result">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-400 font-semibold">
              <Sparkles className="h-4 w-4" /> AI Live Insight
              <span className="ml-2 normal-case tracking-normal text-[10px] text-zinc-500 font-normal">
                AI-generated · verify against Cisco Bug Search before acting
              </span>
            </div>
            <div className="mt-3 text-sm text-zinc-300 leading-relaxed space-y-1.5">
              {ai.split("\n").filter((l) => l.trim()).map((line, i) => (
                <p key={i} className={line.trim().match(/^[-*•]/) ? "pl-4 relative before:content-['▸'] before:absolute before:left-0 before:text-blue-400" : "font-medium text-white mt-2"}>
                  {line.replace(/^[-*•]\s*/, "").replace(/\*\*/g, "").replace(/^#+\s*/, "")}
                </p>
              ))}
            </div>
          </div>
        )}

        {loading && <div className="mt-6 text-zinc-500 font-mono text-sm">Querying knowledge base…</div>}

        {/* Result */}
        {result && (
          <div className="mt-6 space-y-4" data-testid="firmware-result">
            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-6">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-md bg-zinc-950 border border-zinc-800 flex items-center justify-center">
                  <Cpu className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white">{result.model}</h2>
                  <p className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">{result.series} · {result.protocol}</p>
                </div>
              </div>
              <div className="mt-4 rounded-md bg-zinc-950 border border-zinc-800 border-l-4 border-l-emerald-500 p-3">
                <div className="text-[11px] uppercase tracking-wider text-emerald-400 font-semibold">Recommended Load</div>
                <div className="font-mono text-sm text-zinc-200 mt-1">{result.recommended_load}</div>
              </div>
            </div>

            {result.matched_version && <FirmwareCard fw={result.matched_version} highlight />}

            {(() => {
              const rest = result.all_versions.filter(
                (fw) => !result.matched_version || fw.version !== result.matched_version.version
              );
              if (rest.length === 0) return null;
              return (
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-3">
                    {result.matched_version ? "Other Tracked Versions" : "Tracked Versions"}
                  </div>
                  <div className="space-y-3">
                    {rest.map((fw) => <FirmwareCard key={fw.version} fw={fw} />)}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Cached AI insights */}
        {insights.length > 0 && (
          <div className="mt-10" data-testid="insight-cache">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-3">
              <History className="h-4 w-4" /> Recent AI Insights ({insights.length})
              <span className="ml-1 normal-case tracking-normal text-[10px] text-zinc-600 font-normal">cached · no re-query needed</span>
            </div>
            <div className="space-y-2">
              {insights.map((ins, i) => {
                const key = ins.cache_key || `${ins.model}-${i}`;
                const open = openInsight === key;
                return (
                  <div key={key} data-testid={`insight-item-${i}`} className="rounded-lg border border-zinc-800 bg-zinc-900 overflow-hidden">
                    <button
                      onClick={() => setOpenInsight(open ? null : key)}
                      data-testid={`insight-toggle-${i}`}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-zinc-800/40 transition-colors"
                    >
                      <div className="min-w-0">
                        <div className="text-sm text-white font-medium truncate">
                          {ins.model}{ins.version ? <span className="text-zinc-500 font-mono"> · {ins.version}</span> : null}
                        </div>
                        <div className="text-[11px] text-zinc-500">{fmtTime(ins.created_at)}</div>
                      </div>
                      <ChevronDown className={`h-4 w-4 text-zinc-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                    </button>
                    {open && (
                      <div className="px-4 pb-4 pt-1 border-t border-zinc-800 text-sm text-zinc-300 leading-relaxed space-y-1.5" data-testid={`insight-body-${i}`}>
                        {renderMd(ins.insight)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const FirmwareCard = ({ fw, highlight }) => {
  const st = STATUS[fw.status] || STATUS.stable;
  return (
    <div
      data-testid={`firmware-card-${fw.version}`}
      className={`rounded-lg border bg-zinc-900 p-5 ${highlight ? "border-blue-500/40 ring-1 ring-blue-500/20" : "border-zinc-800"}`}
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-mono text-lg font-bold text-white">{fw.version}</div>
        <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full border flex items-center gap-1.5 ${st.cls}`}>
          <st.Icon className="h-3.5 w-3.5" /> {st.label}
        </span>
      </div>
      <p className="text-sm text-zinc-400 mt-2">{fw.notes}</p>

      {fw.bugs?.length > 0 && (
        <div className="mt-4 space-y-2">
          {fw.bugs.map((b) => (
            <div key={b.id} className="rounded-md bg-zinc-950 border border-zinc-800 p-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="flex items-center gap-2 font-mono text-sm text-zinc-200">
                  <Bug className="h-4 w-4 text-red-400" /> {b.id}
                </span>
                <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full border ${SEV_CLS[b.severity]}`}>{b.severity}</span>
              </div>
              <p className="text-sm text-zinc-400 mt-1.5">{b.summary}</p>
              <p className="text-xs text-emerald-400 mt-1">Fixed in: <span className="font-mono">{b.fixed_in}</span></p>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-zinc-800 text-sm">
        <span className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">Recommendation: </span>
        <span className="text-zinc-300">{fw.recommendation}</span>
      </div>
    </div>
  );
};
